import { supabase } from '@/lib/integrations/supabase'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import { getAccountingDocumentMovement, taxIdsRepresentSameCompany } from '@/modules/accounting/accountingDocumentMovement'
import { getDocumentDownloadUrl, type StoredDocument } from '@/modules/documents'
import {
  createLayoutDefinitionSkeleton,
  definitionFromFileLayout,
  executeFileLayout,
  getStoredDocumentType,
  hasBuiltInFiscalLayout,
  isLayoutCompatibleWithDocument,
  listFileLayouts,
  resolveExecutedFileLayout,
  type DeclarativeFileLayout,
  type ResolvedFileMapping,
} from '@/modules/file-reader'

export interface OutgoingBookkeepingPreview {
  document: StoredDocument
  movement: Record<string, unknown>
  items: Array<Record<string, unknown>>
  warnings: string[]
  readerKey: string
  readerVersion: string
  mappingVersion: string
  layoutName: string
}

export interface AppliedMovementTax {
  id: string
  tax_code: string
  code_system: string | null
  situation_code: string | null
  base_amount: number | null
  base_reduction_rate: number | null
  nominal_rate: number | null
  effective_rate: number | null
  tax_amount: number | null
  credit_base_amount: number | null
  credit_rate: number | null
  credit_amount: number | null
  relief_amount: number | null
  source: string
  attrs: Record<string, unknown>
}

export interface BookkeptMovementItem {
  id: string
  line_number: number | null
  external_code: string | null
  description: string | null
  quantity: number
  uom: string
  unit_cost: number | null
  total_cost: number | null
  attrs: Record<string, unknown>
  movement_taxes: AppliedMovementTax[]
}

export interface AccountingSourceDocumentItem {
  line_number: number | null
  external_code: string | null
  description: string | null
  quantity: number | null
  uom: string | null
  unit_cost: number | null
  total_cost: number | null
  attrs: Record<string, unknown>
}

export interface AccountingDocumentBookkeepingDetails {
  id: string
  movement_date: string
  description: string | null
  notes: string | null
  status: string
  attrs: Record<string, unknown>
  movement_types: { key: string; name: string } | null
  movement_taxes: AppliedMovementTax[]
  movement_items: BookkeptMovementItem[]
}

export interface AccountingDocumentBookkeepingCfopSummary {
  document_id: string
  cfops: Array<{ code: string; count: number }>
}

export interface AccountingDocumentTaxSummaryRow {
  document_id: string
  cfop: string | null
  situation_code: string | null
  rate: number | null
  total_amount: number
  base_amount: number | null
  tax_amount: number | null
  item_count: number
}

const optionalText = (value: unknown) => typeof value === 'string' && value.trim()
  ? value.trim()
  : null

const optionalNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null
  const number = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(number) ? number : null
}

export async function getAccountingSourceDocumentItems(
  officeUnitId: string,
  document: StoredDocument,
): Promise<AccountingSourceDocumentItem[]> {
  if (!document.storage_path || document.document_kind !== 'xml') return []

  const response = await fetch(await getDocumentDownloadUrl(document.storage_path))
  if (!response.ok) throw new Error('accounting_bookkeeping_download_failed')

  const buffer = await response.arrayBuffer()
  const documentType = getStoredDocumentType(document)
  const layouts = await listFileLayouts(officeUnitId)
  const layout = layouts.find((candidate) => {
    const definition = definitionFromFileLayout(candidate)
    return definition.direction !== 'export'
      && isLayoutCompatibleWithDocument(candidate, document)
  }) || null
  const definition: DeclarativeFileLayout | null = layout
    ? definitionFromFileLayout(layout)
    : hasBuiltInFiscalLayout(documentType)
      ? createLayoutDefinitionSkeleton({ type: documentType })
      : null

  if (!definition) return []

  const resolved = resolveExecutedFileLayout(definition, executeFileLayout(buffer, definition))
  return resolved.movementItems.map((item, index) => ({
    line_number: optionalNumber(item.line_number) ?? index + 1,
    external_code: optionalText(item.external_code),
    description: optionalText(item.description),
    quantity: optionalNumber(item.quantity),
    uom: optionalText(item.uom),
    unit_cost: optionalNumber(item.unit_cost),
    total_cost: optionalNumber(item.total_cost),
    attrs: item.attrs && typeof item.attrs === 'object'
      ? item.attrs as Record<string, unknown>
      : {},
  }))
}

function withBookkeepingMetadata(
  resolved: ResolvedFileMapping,
  document: StoredDocument,
): Record<string, unknown> {
  const attrs = resolved.movement.attrs && typeof resolved.movement.attrs === 'object'
    ? resolved.movement.attrs as Record<string, unknown>
    : {}
  const documentNumber = typeof attrs.document_number === 'string' ? attrs.document_number : null
  const documentType = getStoredDocumentType(document)

  return {
    ...resolved.movement,
    description: resolved.movement.description || [
      'Saída fiscal',
      documentType === 'nfe' ? 'NF-e' : documentType === 'cte' ? 'CT-e' : 'documento',
      documentNumber,
    ].filter(Boolean).join(' '),
    status: 'confirmed',
    attrs: {
      ...attrs,
      bookkeeping_direction: 'outgoing',
      fiscal_document_type: documentType,
    },
  }
}

export async function prepareOutgoingBookkeeping(
  officeUnitId: string,
  client: AccountingClient,
  document: StoredDocument,
): Promise<OutgoingBookkeepingPreview> {
  if (getAccountingDocumentMovement(document, client.cnpj) !== 'outgoing') {
    throw new Error('accounting_bookkeeping_outgoing_required')
  }
  if (!document.storage_path || document.document_kind !== 'xml') {
    throw new Error('accounting_bookkeeping_source_unavailable')
  }

  const response = await fetch(await getDocumentDownloadUrl(document.storage_path))
  if (!response.ok) throw new Error('accounting_bookkeeping_download_failed')
  const buffer = await response.arrayBuffer()
  const documentType = getStoredDocumentType(document)
  const layouts = await listFileLayouts(officeUnitId)
  const layout = layouts.find((candidate) => {
    const definition = definitionFromFileLayout(candidate)
    return definition.direction !== 'export'
      && isLayoutCompatibleWithDocument(candidate, document)
  }) || null
  const definition: DeclarativeFileLayout | null = layout
    ? definitionFromFileLayout(layout)
    : hasBuiltInFiscalLayout(documentType)
      ? createLayoutDefinitionSkeleton({ type: documentType })
      : null
  if (!definition) throw new Error('accounting_bookkeeping_layout_required')

  const resolved = resolveExecutedFileLayout(definition, executeFileLayout(buffer, definition))
  const movement = withBookkeepingMetadata(resolved, document)
  const movementAttrs = movement.attrs as Record<string, unknown>
  const operationType = String(movementAttrs.operation_type ?? '')

  if (!taxIdsRepresentSameCompany(client.cnpj, movementAttrs.issuer_tax_id)) {
    throw new Error('accounting_bookkeeping_outgoing_required')
  }
  if (documentType === 'nfe' && operationType !== '1') {
    throw new Error('accounting_bookkeeping_outgoing_required')
  }

  if (typeof movement.movement_date !== 'string' || !movement.movement_date) {
    resolved.warnings.push('missing_required:movement_date')
  }
  if (typeof movementAttrs.document_number !== 'string' || !movementAttrs.document_number) {
    resolved.warnings.push('missing_required:document_number')
  }
  if (typeof movementAttrs.total_amount !== 'number') {
    resolved.warnings.push('missing_required:total_amount')
  }
  const invalidItems = resolved.movementItems.filter((item) => (
    typeof item.quantity !== 'number' || item.quantity <= 0
  ))
  if (invalidItems.length > 0) resolved.warnings.push(`invalid_item_quantity:${invalidItems.length}`)

  return {
    document,
    movement,
    items: resolved.movementItems,
    warnings: resolved.warnings,
    readerKey: definition.key,
    readerVersion: String(definition.schemaVersion),
    mappingVersion: layout?.latest_version?.version
      ? String(layout.latest_version.version)
      : definition.layoutVersion,
    layoutName: definition.name,
  }
}

export async function confirmOutgoingBookkeeping(
  preview: OutgoingBookkeepingPreview,
): Promise<string> {
  const { data, error } = await supabase.rpc('bookkeep_accounting_document', {
    p_document_id: preview.document.id,
    p_movement: preview.movement,
    p_items: preview.items,
    p_trace: {
      direction: 'outgoing',
      reader_key: preview.readerKey,
      reader_version: preview.readerVersion,
      mapping_version: preview.mappingVersion,
      layout_name: preview.layoutName,
      warnings: preview.warnings,
      copied_without_transformation: true,
    },
  })

  if (error) throw error
  return String(data)
}

export function hasBlockingBookkeepingWarnings(warnings: string[]) {
  return warnings.some((warning) => (
    warning.startsWith('invalid_item_quantity') || warning.startsWith('missing_required:')
  ))
}

export async function clearAccountingClientBookkeeping(clientUnitId: string): Promise<number> {
  const { data, error } = await supabase.rpc('clear_accounting_client_bookkeeping', {
    p_client_unit_id: clientUnitId,
  })

  if (error) throw error
  return Number(data || 0)
}

export async function listAccountingDocumentBookkeepingCfops(
  clientUnitId: string,
): Promise<AccountingDocumentBookkeepingCfopSummary[]> {
  const { data, error } = await supabase.rpc('list_accounting_document_bookkeeping_cfops', {
    p_client_unit_id: clientUnitId,
  })
  if (error) throw error
  return (data || []).map((row: Record<string, unknown>) => ({
    document_id: String(row.document_id || ''),
    cfops: Array.isArray(row.cfops)
      ? row.cfops.flatMap((entry) => {
          if (!entry || typeof entry !== 'object') return []
          const value = entry as Record<string, unknown>
          const code = typeof value.code === 'string' ? value.code : ''
          return code ? [{ code, count: Number(value.count || 0) }] : []
        })
      : [],
  }))
}

export async function listAccountingDocumentTaxSummary(
  clientUnitId: string,
  taxCode: string,
): Promise<AccountingDocumentTaxSummaryRow[]> {
  const { data, error } = await supabase.rpc('list_accounting_document_tax_summary', {
    p_client_unit_id: clientUnitId,
    p_tax_code: taxCode,
  })
  if (error) throw error
  return (data || []).map((row: Record<string, unknown>) => ({
    document_id: String(row.document_id || ''),
    cfop: optionalText(row.cfop),
    situation_code: optionalText(row.situation_code),
    rate: optionalNumber(row.rate),
    total_amount: optionalNumber(row.total_amount) || 0,
    base_amount: optionalNumber(row.base_amount),
    tax_amount: optionalNumber(row.tax_amount),
    item_count: optionalNumber(row.item_count) || 0,
  }))
}

export async function getAccountingDocumentBookkeepingDetails(
  clientUnitId: string,
  documentId: string,
): Promise<AccountingDocumentBookkeepingDetails | null> {
  const { data, error } = await supabase
    .from('movements')
    .select(`
      id,
      movement_date,
      description,
      notes,
      status,
      attrs,
      movement_types(key, name),
      movement_taxes(*),
      movement_items(
        id,
        line_number,
        external_code,
        description,
        quantity,
        uom,
        unit_cost,
        total_cost,
        attrs,
        movement_taxes(*)
      )
    `)
    .eq('unit_id', clientUnitId)
    .eq('attrs->source->>document_id', documentId)
    .eq('attrs->bookkeeping->>status', 'booked')
    .neq('status', 'canceled')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return data as AccountingDocumentBookkeepingDetails | null
}
